import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VideoRoom } from '@theraply/ui';
import {
  useCreateRoom,
  useJoinSession,
  useVideoRoom,
  useEndSession,
  useVideoRoomExists,
} from '@theraply/api-client';
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

/**
 * Video session page for therapists
 * Handles creating rooms, joining sessions, and ending sessions with host controls
 */
export const VideoSessionPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();

  const [joinToken, setJoinToken] = useState<string | null>(null);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  const bookingIdNum = bookingId ? parseInt(bookingId, 10) : null;

  // Check if room exists
  const { data: roomExists, isLoading: isCheckingRoom } = useVideoRoomExists(bookingIdNum);

  // Fetch video room information
  const { data: videoRoom, isLoading: isLoadingRoom } = useVideoRoom(bookingIdNum);

  // Mutations
  const createRoomMutation = useCreateRoom();
  const joinMutation = useJoinSession();
  const endSessionMutation = useEndSession();

  // Create room if it doesn't exist
  useEffect(() => {
    if (!bookingIdNum || isCheckingRoom || roomExists === undefined) return;

    if (!roomExists && !createRoomMutation.isPending && !createRoomMutation.isSuccess) {
      createRoomMutation.mutate(
        { bookingId: bookingIdNum },
        {
          onError: (err: any) => {
            setError(err.response?.data?.message || 'Failed to create video room');
          },
        }
      );
    }
  }, [bookingIdNum, roomExists, isCheckingRoom]);

  // Auto-join when room is available
  useEffect(() => {
    if (!bookingIdNum || !videoRoom || joinToken) return;

    joinMutation.mutate(bookingIdNum, {
      onSuccess: (data) => {
        setJoinToken(data.token);
        setRoomUrl(data.roomUrl);
        setSessionStartTime(new Date());
      },
      onError: (err: any) => {
        setError(err.response?.data?.message || 'Failed to join session');
      },
    });
  }, [bookingIdNum, videoRoom, joinToken]);

  const handleEndSession = async () => {
    if (!bookingIdNum) return;

    if (window.confirm('Are you sure you want to end this session? This action cannot be undone.')) {
      endSessionMutation.mutate(bookingIdNum, {
        onSuccess: () => {
          navigate('/dashboard');
        },
        onError: (err: any) => {
          alert(err.response?.data?.message || 'Failed to end session');
        },
      });
    }
  };

  const handleLeave = () => {
    handleEndSession();
  };

  // Loading state
  if (isCheckingRoom || isLoadingRoom || createRoomMutation.isPending || (!joinToken && !error)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center text-white max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-2xl font-semibold mb-2">Setting up your session...</h2>
          <p className="text-gray-400 mb-4">Creating secure video room</p>
          <div className="bg-gray-800 rounded-lg p-4 text-left">
            <h3 className="font-semibold mb-2">Tips for a great session:</h3>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• Ensure good lighting</li>
              <li>• Use headphones to prevent echo</li>
              <li>• Find a quiet, private space</li>
              <li>• Test your camera and microphone</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="max-w-md w-full mx-4">
          <div className="bg-gray-800 rounded-lg shadow-xl p-8 text-center">
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">Unable to Start Session</h2>
            <p className="text-gray-300 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Video session active
  if (joinToken && roomUrl) {
    return (
      <div className="relative">
        {/* Session timer in top right */}
        {sessionStartTime && (
          <SessionTimer startTime={sessionStartTime} />
        )}

        <VideoRoom
          roomUrl={roomUrl}
          token={joinToken}
          userName="Therapist"
          onLeave={handleLeave}
          onError={(err) => setError(err.message)}
          showControls={true}
          isHost={true}
        />
      </div>
    );
  }

  return null;
};

interface SessionTimerProps {
  startTime: Date;
}

/**
 * Session timer component showing elapsed time
 */
const SessionTimer: React.FC<SessionTimerProps> = ({ startTime }) => {
  const [elapsed, setElapsed] = useState<string>('00:00');

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const diff = now.getTime() - startTime.getTime();
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setElapsed(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <div className="absolute top-4 right-4 z-20 bg-gray-800 rounded-lg px-4 py-2 shadow-lg border border-gray-700">
      <div className="flex items-center space-x-2 text-white">
        <ClockIcon className="w-5 h-5" />
        <span className="font-mono font-semibold">{elapsed}</span>
      </div>
    </div>
  );
};
